/**
 * Buntu Companion — end-user surface (web placeholder).
 *
 * This is the screen the native tray app will mirror. It registers a companion
 * session, keeps the token in memory only (never localStorage), and renders the
 * sections the tenant configuration enables.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  Bell,
  CheckCircle,
  Clock,
  Laptop,
  LifeBuoy,
  Package,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';

const API_BASE = typeof window !== 'undefined' ? `${window.location.origin}/api/v1` : '/api/v1';
const COMPANION_ID_KEY = 'buntu_companion_id';

/**
 * Backend `detail` codes that mean the session token is no longer usable. These
 * are wire codes, not messages: showing one to an end user reads as a fault they
 * caused, when the answer is simply to get a new token.
 */
const DEAD_TOKEN_CODES = new Set([
  'companion_token_required',
  'companion_token_invalid',
  'companion_token_revoked',
  'companion_token_expired',
]);

/** Carries the backend's wire code alongside the text shown to the user. */
class CompanionApiError extends Error {
  code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'CompanionApiError';
    this.code = code;
  }
}

const isDeadToken = (err: unknown): boolean =>
  !!err && DEAD_TOKEN_CODES.has(String((err as CompanionApiError).code || ''));

/** Stable per-browser install id. Not a secret: the session token is separate. */
function companionId(): string {
  try {
    const existing = localStorage.getItem(COMPANION_ID_KEY);
    if (existing) return existing;
    const generated = `web-${(crypto as any)?.randomUUID?.() || Math.random().toString(36).slice(2)}`;
    localStorage.setItem(COMPANION_ID_KEY, generated);
    return generated;
  } catch {
    return `web-${Math.random().toString(36).slice(2)}`;
  }
}

interface Props {
  /** Hub JWT used once to register; the companion token is used from then on. */
  hubToken: string | null;
  /** Optional device to bind this companion to. */
  deviceId?: string | null;
  /**
   * A companion session token the caller already holds. The desktop host
   * registers with the agent's device identity and injects the result, so the
   * tray never asks the user to sign in.
   */
  sessionToken?: string | null;
}

const Section: React.FC<{ title: string; icon?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  icon,
  children,
}) => (
  <section className="min-w-0 max-w-full overflow-hidden rounded-lg border border-slate-200 bg-white p-3 sm:p-4">
    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
      {icon}
      {title}
    </h3>
    {children}
  </section>
);

const Pill: React.FC<{ tone: 'green' | 'amber' | 'red' | 'slate'; children: React.ReactNode }> = ({
  tone,
  children,
}) => {
  const tones: Record<string, string> = {
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    slate: 'bg-slate-50 text-slate-600 border-slate-200',
  };
  return (
    <span className={`inline-flex max-w-full items-center break-words rounded-full border px-2 py-0.5 text-center text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
};

const CompanionUserApp: React.FC<Props> = ({ hubToken, deviceId, sessionToken }) => {
  // In-memory only: desktop and mobile builds must use the OS keychain instead.
  const [token, setToken] = useState<string | null>(sessionToken || null);
  const [me, setMe] = useState<any>(null);
  const [home, setHome] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [ticket, setTicket] = useState({ subject: '', description: '', category: '', priority: '' });
  const [tickets, setTickets] = useState<any[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [chat, setChat] = useState<{ question: string; reply: string | null }>({ question: '', reply: null });
  const loadedBundleRef = useRef<string | null>(null);
  const loadedTokenRef = useRef<string | undefined>(undefined);
  // Set when the token is rejected and we cannot mint a new one ourselves. It
  // stops the 30s poll from retrying a token the server has already refused.
  const [tokenDead, setTokenDead] = useState(false);

  // The host can hand over a fresh token after re-registering (session expiry).
  useEffect(() => {
    if (sessionToken) setToken(sessionToken);
  }, [sessionToken]);

  // The desktop host holds the only credential that can mint a session, and the
  // page it opened may outlive several of them. Expose a hook so the host can
  // hand over a refreshed token in place, rather than the page having to reload
  // to re-read the value injected when the window was opened.
  const applyHostToken = useCallback((next: unknown) => {
    if (typeof next !== 'string' || !next) return;
    setToken(next);
    setTokenDead(false);
    setError(null);
  }, []);

  useEffect(() => {
    const w = window as unknown as { __buntuCompanionSetToken?: (t: unknown) => void };
    w.__buntuCompanionSetToken = applyHostToken;
    return () => {
      delete w.__buntuCompanionSetToken;
    };
  }, [applyHostToken]);

  // The desktop host keeps this window alive when it is hidden. Detect a newly
  // deployed Hub bundle and reload the hosted portal automatically, so endpoint
  // users do not need to upgrade or restart the Companion for UI-only changes.
  useEffect(() => {
    let stopped = false;
    const checkForUiUpdate = async () => {
      try {
        const res = await fetch(`/asset-manifest.json?v=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const manifest = await res.json();
        const bundle = String(manifest?.files?.['main.js'] || '');
        if (!bundle || stopped) return;
        if (loadedBundleRef.current && loadedBundleRef.current !== bundle) {
          window.location.reload();
          return;
        }
        loadedBundleRef.current = bundle;
      } catch {
        // UI version checks are best-effort; normal Companion polling continues.
      }
    };
    checkForUiUpdate();
    const id = window.setInterval(checkForUiUpdate, 60000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkForUiUpdate();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const call = useCallback(
    async (path: string, init?: RequestInit, bearer?: string | null) => {
      const headers = new Headers(init?.headers);
      const auth = bearer ?? token;
      if (auth) headers.set('Authorization', `Bearer ${auth}`);
      if (init?.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
      const res = await fetch(`${API_BASE}/companion${path}`, { ...init, headers });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = payload?.detail;
        let code: string | undefined;
        let message = `Request failed (${res.status})`;
        if (typeof detail === 'string') {
          code = detail.trim();
          message = detail;
        } else if (Array.isArray(detail) && detail[0]?.msg) {
          message = detail[0].msg;
        }
        if (code && DEAD_TOKEN_CODES.has(code)) {
          message = 'Your Companion session has expired.';
        }
        throw new CompanionApiError(message, code);
      }
      return payload;
    },
    [token]
  );

  const register = useCallback(async () => {
    if (!hubToken) throw new Error('Your companion session has ended. Please sign in again.');
    const res = await call(
      '/register',
      {
        method: 'POST',
        body: JSON.stringify({
          companion_id: companionId(),
          os_type: 'web',
          install_mode: 'web',
          app_version: '0.1.0',
          device_id: deviceId || null,
        }),
      },
      hubToken
    );
    const issued = res?.data?.token;
    if (!issued) throw new Error('Companion registration did not return a token.');
    setToken(issued);
    return issued;
  }, [call, hubToken, deviceId]);

  const fetchBundle = useCallback(async (bearer: string) => {
    const [meRes, homeRes, ticketsRes] = await Promise.all([
      call('/me', undefined, bearer),
      call('/home', undefined, bearer),
      call('/support-tickets?limit=20', undefined, bearer).catch(() => null),
    ]);
    setMe(meRes?.data || null);
    setHome(homeRes?.data || null);
    setTickets(ticketsRes?.data?.items || []);
  }, [call]);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const bearer = token || (await register());
      loadedTokenRef.current = bearer;
      try {
        await fetchBundle(bearer);
      } catch (e: any) {
        // A token can die while the portal stays open — sessions expire, and IT
        // can revoke them. In the browser we hold the Hub JWT and can quietly
        // mint a new one; the desktop host does not give the page that
        // credential, so there it can only ask the host for a fresh session.
        if (!isDeadToken(e)) throw e;
        if (!hubToken) {
          setToken(null);
          setTokenDead(true);
          // Reported even on a silent poll: the alternative is a portal that
          // quietly stops updating and shows yesterday's data as if it were now.
          setError(
            'Your Companion session has expired. Reopen Buntu from the system tray to reconnect.'
          );
          return;
        }
        await fetchBundle(await register());
      }
      setTokenDead(false);
    } catch (e: any) {
      if (!opts?.silent) setError(e?.message || 'Could not load your companion.');
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [fetchBundle, hubToken, register, token]);

  // Loads on mount, and again whenever the token changes — the host swaps in a
  // refreshed session without reloading the page, and the new one has to be put
  // to use. Skipped once the token is known dead, so a portal waiting on the
  // host does not retry a credential the server has already refused.
  useEffect(() => {
    if (tokenDead) return;
    if (token && loadedTokenRef.current === token) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, tokenDead]);

  // Keep the inbox fresh while the portal stays open, so a deploy that IT
  // starts after sign-in shows up without a reload.
  useEffect(() => {
    if (!token || tokenDead) return undefined;
    const id = window.setInterval(() => {
      load({ silent: true }).catch(() => {
        /* next tick retries */
      });
    }, 30000);
    return () => window.clearInterval(id);
  }, [token, tokenDead, load]);

  const features = useMemo(() => home?.features || me?.features || {}, [home, me]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = (window.location.hash || '').replace('#', '').toLowerCase();
    if (hash !== 'tickets' && hash !== 'support') return;
    const el = document.getElementById('companion-tickets');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [features.support_tickets, home]);
  const device = home?.device;
  const compliance = home?.compliance;
  const patches = home?.patches;
  const apps = home?.apps;
  const notifications = home?.notifications;
  const consents = home?.pending_consents || [];

  const requiredActions = useMemo(() => {
    const actions: string[] = [];
    if (features.reboot_notice && device?.reboot_required) actions.push('Restart your device to finish updates.');
    if (features.compliance && compliance?.status && compliance.status !== 'compliant') {
      actions.push('Your device is not compliant. Contact IT if this persists.');
    }
    if (features.patches && (patches?.pending_count || 0) > 0) {
      actions.push(`${patches.pending_count} update(s) waiting to install.`);
    }
    return actions;
  }, [features, device, compliance, patches]);

  const submitTicket = async () => {
    setError(null);
    const subject = ticket.subject.trim();
    if (subject.length < 3) {
      setError('Tell IT what you need in a few words first.');
      return;
    }
    try {
      setTicketSubmitting(true);
      // An empty description is fine: the API (and we) fall back to the subject
      // so a short "please install Firefox" ask does not bounce with a 422.
      const description = ticket.description.trim() || subject;
      const res = await call('/support-tickets', {
        method: 'POST',
        body: JSON.stringify({
          subject,
          description,
          category: ticket.category || undefined,
          priority: ticket.priority || undefined,
        }),
      });
      setNotice(`Ticket ${res?.data?.ticket_no} raised. IT will get back to you.`);
      setTicket({ subject: '', description: '', category: '', priority: '' });
      const ticketsRes = await call('/support-tickets?limit=20');
      setTickets(ticketsRes?.data?.items || []);
    } catch (e: any) {
      setError(e?.message || 'Could not raise the ticket.');
    } finally {
      setTicketSubmitting(false);
    }
  };

  const refreshTickets = async () => {
    setTicketsLoading(true);
    setError(null);
    try {
      const res = await call('/support-tickets?limit=20');
      setTickets(res?.data?.items || []);
    } catch (e: any) {
      setError(e?.message || 'Could not refresh your tickets.');
    } finally {
      setTicketsLoading(false);
    }
  };

  const syncNow = async () => {
    setError(null);
    try {
      const res = await call('/sync-now', { method: 'POST' });
      setNotice(res?.data?.queued ? 'Sync requested. Your device will check in shortly.' : 'A sync is already running.');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Could not request a sync.');
    }
  };

  const respondConsent = async (consentId: string, decision: 'approved' | 'denied') => {
    setError(null);
    try {
      await call('/remote-support/consent', {
        method: 'POST',
        body: JSON.stringify({ consent_id: consentId, decision }),
      });
      setNotice(decision === 'approved' ? 'Remote support approved.' : 'Remote support declined.');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Could not record your response.');
    }
  };

  const askBuntu = async () => {
    setError(null);
    try {
      const res = await call('/chat', { method: 'POST', body: JSON.stringify({ message: chat.question }) });
      setChat((prev) => ({ ...prev, reply: res?.data?.reply || null }));
    } catch (e: any) {
      setError(e?.message || 'Ask Buntu is unavailable.');
    }
  };

  if (loading) {
    return <div className="py-16 text-center text-sm text-slate-500">Starting your companion…</div>;
  }

  return (
    <div className="mx-auto w-full min-w-0 max-w-4xl space-y-3 overflow-x-hidden p-2 sm:space-y-4 sm:p-4">
      <header className="min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-white p-3 sm:p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-slate-800">{me?.greeting || 'Hello'}</h2>
            <p className="mt-1 break-words text-sm text-slate-500">
              {me?.branding?.companion_name || 'Buntu Companion'}
              {device?.name ? ` · ${device.name}` : ''}
              {device?.os ? ` · ${device.os}${device.os_version ? ` ${device.os_version}` : ''}` : ''}
            </p>
            {device?.last_sync && (
              <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400">
                <Clock size={12} /> Last sync {new Date(device.last_sync).toLocaleString()}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {features.compliance && compliance?.status && (
              <Pill tone={compliance.status === 'compliant' ? 'green' : compliance.status === 'unknown' ? 'slate' : 'red'}>
                {compliance.status === 'compliant' ? 'Compliant' : compliance.status}
              </Pill>
            )}
            {features.sync_now && (
              <button
                type="button"
                onClick={syncNow}
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              >
                <RefreshCw size={14} /> Sync now
              </button>
            )}
          </div>
        </div>
      </header>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle size={16} className="mt-0.5" />
          {error}
        </div>
      )}
      {notice && (
        <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          <CheckCircle size={16} className="mt-0.5" />
          {notice}
        </div>
      )}

      {features.remote_support_consent && consents.length > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-blue-900">
            <ShieldCheck size={16} /> IT is asking to connect to your device
          </h3>
          {consents.map((c: any) => (
            <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 border-t border-blue-100 py-2">
              <div className="text-sm text-blue-900">
                <p className="font-medium">{c.request_type.replace(/_/g, ' ')}</p>
                {c.metadata_json?.reason && <p className="text-xs">{c.metadata_json.reason}</p>}
                {c.expires_at && (
                  <p className="text-xs text-blue-700">Expires {new Date(c.expires_at).toLocaleTimeString()}</p>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => respondConsent(c.id, 'approved')}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                >
                  Allow
                </button>
                <button
                  type="button"
                  onClick={() => respondConsent(c.id, 'denied')}
                  className="rounded-md border border-blue-300 px-3 py-1.5 text-xs text-blue-800 hover:bg-blue-100"
                >
                  Not now
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {requiredActions.length > 0 && (
        <Section title="Needs your attention" icon={<AlertCircle size={16} className="text-amber-500" />}>
          <ul className="space-y-1 text-sm text-slate-700">
            {requiredActions.map((action) => (
              <li key={action} className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                    <span className="min-w-0 break-words">{action}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <div className="grid min-w-0 gap-3 sm:gap-4 md:grid-cols-2">
        {features.my_device && (
          <Section title="My device" icon={<Laptop size={16} className="text-slate-400" />}>
            {device?.bound ? (
              <dl className="space-y-1.5 text-sm">
                {[
                  ['Name', device.name],
                  ['Operating system', `${device.os || ''} ${device.os_version || ''}`.trim()],
                  ['Model', device.model],
                  ['Serial', device.serial_number],
                  ['Agent', device.agent_version],
                  ['Status', device.status],
                ].map(([label, value]) => (
                  <div key={String(label)} className="flex min-w-0 justify-between gap-3">
                    <dt className="shrink-0 text-slate-500">{label}</dt>
                    <dd className="min-w-0 break-words text-right text-slate-800">{value || '—'}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-sm text-slate-500">
                This companion is not linked to a managed device yet.
              </p>
            )}
            {features.reboot_notice && device?.reboot_required && (
              <p className="mt-3 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <RotateCcw size={14} /> A restart is required to finish installing updates.
              </p>
            )}
          </Section>
        )}

        {features.patches && (
          <Section title="Updates" icon={<ShieldCheck size={16} className="text-slate-400" />}>
            <p className="text-sm text-slate-700">
              {patches?.pending_count ?? 0} pending · {patches?.installed_count ?? 0} installed
            </p>
            {patches?.last_scan && (
              <p className="mt-1 text-xs text-slate-400">
                Last checked {new Date(patches.last_scan).toLocaleString()}
              </p>
            )}
            {(patches?.pending || []).length > 0 && (
              <ul className="mt-3 space-y-1 text-sm text-slate-700">
                {patches.pending.slice(0, 5).map((p: any) => (
                  <li key={p.patch_id} className="flex min-w-0 items-center justify-between gap-2">
                    <span className="min-w-0 truncate">{p.title || p.kb_id || p.patch_id}</span>
                    <span className="shrink-0">
                      <Pill tone={p.severity === 'Critical' ? 'red' : 'amber'}>
                        {p.severity && p.severity !== 'Unknown' ? p.severity : (p.status || 'Pending')}
                      </Pill>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        )}

        {features.apps && (
          <Section title="Company apps" icon={<Package size={16} className="text-slate-400" />}>
            {(apps?.items || []).length === 0 ? (
              <p className="text-sm text-slate-500">No apps published for your device yet.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {apps.items.slice(0, 8).map((app: any) => (
                  <li key={app.id} className="flex min-w-0 items-center justify-between gap-2">
                    <span className="min-w-0 truncate text-slate-800">
                      {app.name}
                      {app.version ? ` ${app.version}` : ''}
                    </span>
                    <span className="shrink-0">
                      <Pill tone={app.assigned ? 'green' : 'slate'}>{app.install_status}</Pill>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        )}

        {features.notifications && (
          <Section title="Notifications" icon={<Bell size={16} className="text-slate-400" />}>
            {(notifications?.items || []).length === 0 ? (
              <p className="text-sm text-slate-500">Nothing new.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {notifications.items.map((n: any) => (
                  <li key={n.id} className="rounded border border-slate-100 p-2">
                    <div className="flex min-w-0 items-start justify-between gap-2">
                      <p className="min-w-0 break-words font-medium text-slate-800">{n.title}</p>
                      <span className="shrink-0">
                        <Pill tone={n.severity === 'critical' ? 'red' : n.severity === 'warning' ? 'amber' : 'slate'}>
                          {n.severity}
                        </Pill>
                      </span>
                    </div>
                    <p className="mt-0.5 text-slate-600">{n.message}</p>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        )}
      </div>

      {features.support_tickets && (
        <div id="companion-tickets">
        <Section title="Contact IT" icon={<LifeBuoy size={16} className="text-slate-400" />}>
          <div className="grid min-w-0 gap-3 md:grid-cols-2">
            <input
              type="text"
              placeholder="What do you need help with?"
              value={ticket.subject}
              onChange={(e) => setTicket({ ...ticket, subject: e.target.value })}
              className="w-full min-w-0 rounded border border-slate-300 px-3 py-1.5 text-sm md:col-span-2"
            />
            <select
              value={ticket.category}
              onChange={(e) => setTicket({ ...ticket, category: e.target.value })}
              className="w-full min-w-0 rounded border border-slate-300 px-3 py-1.5 text-sm"
            >
              <option value="">Category</option>
              {(me?.support?.categories || []).map((c: string) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select
              value={ticket.priority}
              onChange={(e) => setTicket({ ...ticket, priority: e.target.value })}
              className="w-full min-w-0 rounded border border-slate-300 px-3 py-1.5 text-sm"
            >
              <option value="">Priority</option>
              {(me?.support?.priorities || []).map((p: string) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <textarea
              rows={3}
              placeholder="More detail (optional — we use your request above if you leave this blank)"
              value={ticket.description}
              onChange={(e) => setTicket({ ...ticket, description: e.target.value })}
              className="w-full min-w-0 rounded border border-slate-300 px-3 py-1.5 text-sm md:col-span-2"
            />
          </div>
          <button
            type="button"
            onClick={submitTicket}
            disabled={ticketSubmitting}
            className="mt-3 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {ticketSubmitting ? 'Raising…' : 'Raise ticket'}
          </button>
          <p className="mt-2 text-xs text-slate-500">
            Basic device details are attached so IT does not have to ask for them.
          </p>

          <div className="mt-4 border-t border-slate-200 pt-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-slate-800">My tickets</h4>
                <p className="text-xs text-slate-500">Track requests raised from this companion.</p>
              </div>
              <button
                type="button"
                onClick={refreshTickets}
                disabled={ticketsLoading}
                className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                <RefreshCw size={12} className={ticketsLoading ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>
            {tickets.length === 0 ? (
              <p className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500">
                No tickets raised yet.
              </p>
            ) : (
              <ul className="max-h-64 space-y-2 overflow-y-auto pr-1">
                {tickets.map((item: any) => {
                  const status = String(item.status || 'open').toLowerCase();
                  const tone: 'green' | 'amber' | 'red' | 'slate' =
                    status === 'resolved' || status === 'closed'
                      ? 'green'
                      : status === 'in_progress'
                        ? 'amber'
                        : status === 'cancelled'
                          ? 'red'
                          : 'slate';
                  return (
                    <li key={item.id} className="rounded-md border border-slate-200 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-800">{item.subject}</p>
                          <p className="mt-0.5 text-xs text-slate-500">
                            {item.ticket_no}
                            {item.created_at ? ` · ${new Date(item.created_at).toLocaleString()}` : ''}
                          </p>
                        </div>
                        <Pill tone={tone}>{status.replace(/_/g, ' ')}</Pill>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {item.category && <Pill tone="slate">{item.category}</Pill>}
                        {item.priority && <Pill tone={String(item.priority).toLowerCase() === 'urgent' ? 'red' : 'slate'}>{item.priority}</Pill>}
                      </div>
                      {item.assigned_to && (
                        <p className="mt-2 text-xs text-slate-600">Assigned to {item.assigned_to}</p>
                      )}
                      {item.resolution_note && (
                        <p className="mt-2 rounded bg-emerald-50 px-2 py-1.5 text-xs text-emerald-800">
                          {item.resolution_note}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Section>
        </div>
      )}

      {features.ask_buntu && (
        <Section title="Ask Buntu" icon={<Sparkles size={16} className="text-slate-400" />}>
          <p className="text-sm text-slate-600">{me?.ai?.welcome_text}</p>
          <div className="mt-3 flex min-w-0 flex-col gap-2 sm:flex-row">
            <input
              type="text"
              value={chat.question}
              placeholder="Ask a question"
              onChange={(e) => setChat({ ...chat, question: e.target.value })}
              className="w-full min-w-0 flex-1 rounded border border-slate-300 px-3 py-1.5 text-sm"
            />
            <button
              type="button"
              onClick={askBuntu}
              disabled={!chat.question.trim()}
              className="w-full rounded-md border border-slate-300 px-4 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 sm:w-auto"
            >
              Ask
            </button>
          </div>
          {chat.reply && (
            <p className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              {chat.reply}
            </p>
          )}
        </Section>
      )}
    </div>
  );
};

export default CompanionUserApp;
